/* eslint-disable no-theme-colors/no-theme-colors */
import { createStyles } from '@mantine/core';
import { memo } from 'react';

const useBgStyles = createStyles((theme, { size }: { size: number }) => ({
	bg: {
		borderRadius: theme.radius.xl,
		position: 'absolute',
		top: 0,
		left: 0,
		width: size,
		height: size,
	},
}));

const opacity = 0;

function AIAnimatedBg({
	size,
	speed,
}: {
	size: number;
	speed: 'fast' | 'slow' | 'none';
}) {
	const { classes, theme } = useBgStyles({ size });

	const dur = speed === 'fast' ? '1.5s' : '4s';
	const begin = speed === 'none' ? 'indefinite' : '0s';
	const circleSize = size * 0.8;
	const circleDistanceFromCenter = size * 0.5;

	return (
		<svg
			className={classes.bg}
			width={size}
			height={size}
			viewBox={`0 0 ${size} ${size}`}
			xmlns="http://www.w3.org/2000/svg"
		>
			<defs>
				<radialGradient id="gradient-1">
					<stop
						offset="10%"
						stopColor={theme.other.getColor('icon/ai/default')}
						stopOpacity="1"
					/>
					<stop
						offset="95%"
						stopColor={theme.other.getColor('icon/ai/default')}
						stopOpacity={opacity}
					/>
				</radialGradient>

				<radialGradient id="gradient-2">
					<stop offset="10%" stopColor={theme.colors.cyan[3]} stopOpacity="1" />
					<stop
						offset="95%"
						stopColor={theme.colors.cyan[3]}
						stopOpacity={opacity}
					/>
				</radialGradient>

				<radialGradient id="gradient-3">
					<stop offset="10%" stopColor={theme.colors.pink[1]} stopOpacity="1" />
					<stop
						offset="95%"
						stopColor={theme.colors.pink[1]}
						stopOpacity={opacity}
					/>
				</radialGradient>

				<radialGradient id="gradient-4">
					<stop
						offset="10%"
						stopColor={theme.colors.grape[4]}
						stopOpacity="1"
					/>
					<stop
						offset="95%"
						stopColor={theme.colors.grape[4]}
						stopOpacity={opacity}
					/>
				</radialGradient>
			</defs>

			<circle
				cx={size * 0.8}
				cy={size * 0.5}
				r={circleSize}
				fill="url(#gradient-2)"
			>
				<animateTransform
					attributeName="transform"
					attributeType="XML"
					type="rotate"
					from={`0 ${circleDistanceFromCenter} ${circleDistanceFromCenter}`}
					to={`-360 ${circleDistanceFromCenter} ${circleDistanceFromCenter}`}
					dur={dur}
					repeatCount="indefinite"
					begin={begin}
				/>
			</circle>

			<circle
				cx={size * 0.5}
				cy={size * 0.8}
				r={circleSize}
				fill="url(#gradient-3)"
			>
				<animateTransform
					attributeName="transform"
					attributeType="XML"
					type="rotate"
					from={`0 ${circleDistanceFromCenter} ${circleDistanceFromCenter}`}
					to={`360 ${circleDistanceFromCenter} ${circleDistanceFromCenter}`}
					dur={dur}
					repeatCount="indefinite"
					begin={begin}
				/>
			</circle>

			<circle
				cx={size * 0.2}
				cy={size * 0.5}
				r={circleSize}
				fill="url(#gradient-4)"
			>
				<animateTransform
					attributeName="transform"
					attributeType="XML"
					type="rotate"
					from={`0 ${circleDistanceFromCenter} ${circleDistanceFromCenter}`}
					to={`-360 ${circleDistanceFromCenter} ${circleDistanceFromCenter}`}
					dur={dur}
					repeatCount="indefinite"
					begin={begin}
				/>
			</circle>

			<circle
				cx={size * 0}
				cy={size * 0}
				r={circleSize}
				fill="url(#gradient-1)"
			>
				<animateTransform
					attributeName="transform"
					attributeType="XML"
					type="rotate"
					from={`0 ${circleDistanceFromCenter} ${circleDistanceFromCenter}`}
					to={`360 ${circleDistanceFromCenter} ${circleDistanceFromCenter}`}
					dur={dur}
					repeatCount="indefinite"
					begin={begin}
				/>
			</circle>
		</svg>
	);
}

const useAvatarStyles = createStyles(
	(theme, { avatarSize }: { avatarSize: number }) => ({
		root: {
			position: 'relative',
			width: avatarSize,
			height: avatarSize,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		},
		icon: {
			zIndex: 1,
		},
		animatedIcon: {
			zIndex: 1,
			marginLeft: '-2px',
		},
	})
);

type AnimationSpeed = 'fast' | 'slow' | 'none';

type AIAvatarSize = 'xs' | 'sm' | 'lg' | 'md';

const SIZE_CONFIG = {
	xs: {
		avatarSize: 16,
		iconSize: 12,
	},
	sm: {
		avatarSize: 24,
		iconSize: 16,
	},
	lg: {
		avatarSize: 32,
		iconSize: 20,
	},
	md: {
		avatarSize: 28,
		iconSize: 16,
	},
	illustration: {
		avatarSize: 128,
		iconSize: 80,
	},
} as const;

export interface AIAvatarProps extends React.ComponentPropsWithoutRef<'div'> {
	size: AIAvatarSize | 'illustration' | number;
	speed: AnimationSpeed;
	iconSpeed?: AnimationSpeed;
}

function AIAvatarInternal({
	size = 'md',
	speed = 'fast',
	iconSpeed = speed,
	...props
}: AIAvatarProps) {
	const { avatarSize } = SIZE_CONFIG?.[size as AIAvatarSize] ?? {
		avatarSize: size,
		iconSize: size,
	};

	const { classes } = useAvatarStyles({ avatarSize });

	return (
		<div className={classes.root} style={{ minWidth: avatarSize }} {...props}>
			<AIAnimatedBg size={avatarSize} speed={speed} />
		</div>
	);
}

export const AIAvatar = memo(AIAvatarInternal);
